import Category from './category'
import React, { useEffect, useState } from 'react'
import Loader from 'pageComponents/_common/loader'
import { useLazyQuery } from '@apollo/client'
import { CATEGORY_SEARCH } from '../../../setup/providerGQL'
import { Link, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { cleanSearchState, useDidUpdateEffect } from '../../_common/helpers/generalHelperFunctions'
import AppBarPlugin from '../../SearchResults/plugins/AppBarPlugin'
import BreadCrumbs from 'pageComponents/_common/BreadCrumbs'
import SearchContainer from '../../SearchResults/uiComponents/SearchContainer'
import DrawerPlugin from '../../SearchResults/plugins/DrawerPlugin'
import BrandsPlugin from '../../SearchResults/plugins/BrandsPlugin'
import AvailabilityPlugin from 'pageComponents/SearchResults/plugins/AvailabilityPlugin'
import AttributesPlugin from '../../SearchResults/plugins/AttributesPlugin'
import SortPlugin from '../../SearchResults/plugins/SortPlugin'
import SearchTermsPlugin from '../../SearchResults/plugins/SearchTermsPlugin'
import { useSearchState } from '../../SearchResults/hooks'
import ResultSummaryPlugin from '../../SearchResults/plugins/ResultSummaryPlugin'
import { ArrowForward } from '@mui/icons-material'
import Carousel from '../../_common/Carousel'
import { Helmet } from 'react-helmet'
import { breadcrumbSchema } from '../../_common/richSearchSchemas/breadcrumbSchema'
import { zonedTimeToUtc, format as dateFormat } from 'date-fns-tz'
import PaginationLink from 'pageComponents/_common/PaginationLink'

const DivRow = styled.div`
	display: flex;
	flex-wrap: wrap;
    width: 100%;
    margin-top: 20px;
`

const DivCol = styled.div`
	display: flex;
	flex-direction: column;
	flex-wrap: wrap;
    width: 100%;
`
const BorderContainer = styled.div`
  margin: 20px auto 40px;
  display: flex;
  flex-direction: column;
  width: ${props => {
        const categoryName = props.children[0].props.children
        const approximateLetterWidth = 22 //This makes sure the h1 doesn't overflow off the page for mobile users
        if (!categoryName) {
            return 'max-content'
        } else if (categoryName.length * approximateLetterWidth > window.innerWidth) {
            return 'min-content'
        } else {
            return 'max-content'
        }
    }};
`

const H1 = styled.h1`
	text-transform: uppercase;
	font-size: 32px;
	letter-spacing: 2px;
`

const ShortBorder = styled.div`
  margin: 0 auto;
  border-bottom: 3px solid #B51F2B;
  width: 75%;
`

const RESULT_SIZE = 24

export default function CategorySearch({ categoryUrlSlug, urlPage }) {

    const [categorySearch, setCategorySearch] = useState(null)
    const [resultPage, setResultPage] = useState(1)
    const [lastSearchPayload, setLastSearchPayload] = useState({})

    const [searchState, setSearchState, { setBrands, setAttributes, setSortType, setSearchTerms, setAvailability }] = useSearchState()
    const handleSetSearchState = newSearchData => setSearchState({ ...searchState, ...newSearchData })
    const { results, totalResults, searchTerms, sortType, brands, attributes, isSynced, availability } = searchState

    const categoryName = categorySearch?.category?.name
    const seoHtml = categorySearch?.category?.seoHtml
    const navigate = useNavigate()

    useEffect(() => {
        performCategorySearch()
    }, [resultPage])

    useDidUpdateEffect(() => {
        if (resultPage !== 1) {
            setResultPage(1)
        } else {
            performCategorySearch()
        }
    }, [categoryUrlSlug, sortType, searchTerms])

    useEffect(() => {
        if (urlPage !== resultPage) {
            setResultPage(urlPage)
        }
    }, [urlPage])

    useDidUpdateEffect(() => {
        if (!isSynced) {
            if (resultPage !== 1) {
                setResultPage(1)
            } else {
                performCategorySearch()
            }
        }
    }, [brands, attributes, availability.inStockOnly])

    const performCategorySearch = () => {
        handleSetSearchState({ isSearching: true })
        const payload = {
            searchParams: {
                categorySlug: categoryUrlSlug,
                innerSearchTerms: searchTerms,
                searchType: 'web',
                resultSize: RESULT_SIZE,
                resultPage,
                sortType,
                brands,
                attributes,
                availability
            }
        }
        setLastSearchPayload(payload)
        return categorySearchApiCall({ variables: payload })
    }

    const [categorySearchApiCall, { variables }] = useLazyQuery(CATEGORY_SEARCH, {
        fetchPolicy: 'no-cache',
        onCompleted: ({ categorySearch }) => {
            setCategorySearch(categorySearch)
            if (categorySearch.category?.hasBeenRedirected === true) {
                //redirect from an old category
                navigate(`/categories/${categorySearch.category.urlSlug}/1`)
            }
            if (variables.searchParams === lastSearchPayload.searchParams) {
                const newSearchState = cleanSearchState({ searchState: categorySearch })
                const { result: results, searchTotalCount: totalResults } = categorySearch
                handleSetSearchState({ ...newSearchState, isSynced: true, isSearching: false, results, totalResults })
            }
        }
    })

    const categoryList = (categorySearch?.category?.children || []).map(({ urlSlug, name, imageUrl }) => (
        <Category
            key={urlSlug}
            size={results ? 'small' : 'normal'}
            text={name}
            linkTo={`/categories/${urlSlug}/1`}
            Image={<img src={imageUrl} alt={name} title={name} />}
        />
    ))

    const breadCrumbTrail = categorySearch?.category?.breadCrumbs?.breadcrumbTrail
    //Create the canonical for this page (only displayed if the category isn't redirected)
    const canonUrl = `https://www.airlinehyd.com/categories/${categorySearch?.category?.urlSlug}/${resultPage}`;
    const nextUrl = resultPage !== Math.ceil(totalResults / 24) ?
        `https://www.airlinehyd.com/categories/${categorySearch?.category?.urlSlug}/${resultPage + 1}` :
        null
    const prevUrl = resultPage !== 1 ?
        `https://www.airlinehyd.com/categories/${categorySearch?.category?.urlSlug}/${resultPage - 1}` :
        null


    return !categorySearch
        ? (<Loader />)
        : (<DivCol>
            <Helmet>
                <title>{categoryName || ''} | Airline Hydraulics</title>
                <meta name="description" content={seoHtml ? seoHtml.replace(/<[^>]+>/g, '') : categoryName} />
                <meta httpEquiv="last-modified" content={dateFormat(zonedTimeToUtc(categorySearch?.category?.lastModifiedDate || new Date(), ''), 'EEE, dd LLL yyyy HH:mm:ss') + ' GMT'} />
                {categorySearch.category?.hasBeenRedirected === false && !!prevUrl && <link rel="prev" href={prevUrl} />}
                {categorySearch.category?.hasBeenRedirected === false && !!nextUrl && <link rel="next" href={nextUrl} />}
                {categorySearch.category?.hasBeenRedirected === false && <link rel="canonical" href={canonUrl} />}
                {breadcrumbSchema([
                    {
                        name: 'Categories',
                        item: process.env.REACT_APP_SITE_URL + '/categories'
                    },
                    ...(breadCrumbTrail || []).map(({ urlSlug, name }) => ({
                        name,
                        item: process.env.REACT_APP_SITE_URL + '/categories/' + urlSlug
                    }))
                ])}
            </Helmet>

            <BorderContainer>
                <H1>{categoryName}</H1>
                <ShortBorder />
            </BorderContainer>

            <BreadCrumbs breadCrumbTrail={breadCrumbTrail} />

            {seoHtml && <div style={{ padding: '20px 10px' }} dangerouslySetInnerHTML={{ __html: seoHtml }} />}

            {results && categoryList.length ? (
                <Carousel>
                    <Category size='small' text="More Categories" Image={<ArrowForward style={{ marginTop: 50 }} />} />
                    {categoryList}
                </Carousel>
            ) : (
                <DivRow style={{ justifyContent: 'center' }}>
                    {categoryList}
                </DivRow>
            )}

            {results && (
                <SearchContainer
                    searchState={searchState}
                >
                    <AppBarPlugin
                        title={categoryName}
                    />
                    <DrawerPlugin>
                        <AvailabilityPlugin {...{
                            availability: searchState.availability,
                            setAvailability,
                        }} />
                        <BrandsPlugin
                            brands={brands}
                            setBrands={setBrands}
                        />
                        <AttributesPlugin
                            attributes={attributes}
                            setAttributes={setAttributes}
                        />
                    </DrawerPlugin>
                    <ResultSummaryPlugin
                        searchTerm={categoryName}
                    />
                    <SortPlugin
                        sortType={sortType}
                        setSortType={setSortType}
                    />
                    <SearchTermsPlugin
                        searchTerms={searchTerms.join(',')}
                        setSearchTerms={setSearchTerms}
                    />
                    <PaginationLink
                        page={resultPage}
                        pageSize={RESULT_SIZE}
                        total={totalResults}
                    />
                </SearchContainer>
            )}

            {!results && !categorySearch?.category?.children.length && (
                <EmptyCategory />
            )}
        </DivCol>)
}

const EmptyCategory = () => (
    <>
        <h2>This category is currently empty</h2>
        <p>Let us help: <a href="/pages/contact/contact-us">Customer Service</a></p>
        <p>Or call one of our reps <a href="tel:8009997378">(800) 999-7378</a></p>
        <DivRow><Link to="/categories">Back to all categories</Link></DivRow>
    </>
)
